import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface PropertyRule {
  id: string;
  property_id: string;
  title: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const usePropertyRules = (propertyId: string) => {
  return useQuery({
    queryKey: ['property-rules', propertyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('property_rules')
        .select('*')
        .eq('property_id', propertyId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as PropertyRule[];
    },
    enabled: !!propertyId,
  });
};

export const useCreatePropertyRule = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ propertyId, title }: { propertyId: string; title: string }) => {
      // Get the highest sort_order for this property
      const { data: existingRules } = await supabase
        .from('property_rules')
        .select('sort_order')
        .eq('property_id', propertyId)
        .order('sort_order', { ascending: false })
        .limit(1);

      const nextSortOrder = existingRules && existingRules.length > 0 
        ? existingRules[0].sort_order + 1 
        : 0;

      const { data, error } = await supabase
        .from('property_rules')
        .insert({
          property_id: propertyId,
          title,
          sort_order: nextSortOrder,
        })
        .select()
        .single();

      if (error) throw error;
      return data as PropertyRule;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['property-rules', variables.propertyId] });
      toast({
        title: 'Regla creada',
        description: 'La regla se ha creado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error al crear regla',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useUpdatePropertyRule = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, title, propertyId }: { id: string; title: string; propertyId: string }) => {
      const { data, error } = await supabase
        .from('property_rules')
        .update({ title })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as PropertyRule;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['property-rules', data.property_id] });
      toast({
        title: 'Regla actualizada',
        description: 'La regla se ha actualizado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error al actualizar regla',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useDeletePropertyRule = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, propertyId }: { id: string; propertyId: string }) => {
      const { error } = await supabase
        .from('property_rules')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['property-rules', data.propertyId] });
      toast({
        title: 'Regla eliminada',
        description: 'La regla se ha eliminado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error al eliminar regla',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useReorderPropertyRules = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ rules, propertyId }: { rules: PropertyRule[]; propertyId: string }) => {
      // Update all rules with new sort_order
      const updates = rules.map((rule, index) => 
        supabase
          .from('property_rules')
          .update({ sort_order: index })
          .eq('id', rule.id)
      );

      await Promise.all(updates);
      return { propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['property-rules', data.propertyId] });
      toast({
        title: 'Orden actualizado',
        description: 'El orden de las reglas se ha actualizado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error al reordenar reglas',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};
